
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TableCell, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CampaignActionsIcons } from './CampaignActionsIcons';
import { CampaignStatus } from '@/types/states';
import { ErrorHandler } from '@/services/ErrorHandler';
import { useToast } from '@/hooks/use-toast';

interface DashboardReferent {
  id: string;
  name: string;
  email: string;
  status: string;
  relation_type: string | null;
  invitation_sent_at: string | null;
  last_email_sent_at: string | null;
  email_opens_count: number | null;
  email_clicks_count: number | null;
  reminders_sent_count: number | null;
}

interface DashboardCampaign {
  id: string;
  title: string;
  description: string;
  candidate_name?: string;
  candidate_email?: string;
  target_position?: string;
  status: CampaignStatus | string; // Support both unified enum and legacy strings
  organization_id: string;
  created_at: string;
  updated_at: string;
  evaluation_criteria: string[];
  unique_link?: string | null;
  ai_extracted_data?: any;
  last_activity?: string | null;
  referents_count: number;
  completed_referents: number;
  referents_requested_count?: number; // Nombre de référents demandés
  refused_referents?: number; // Nombre de référents ayant refusé
  is_draft?: boolean;
  referents?: DashboardReferent[];
}

interface CampaignTableRowProps {
  campaign: DashboardCampaign;
  isSelected: boolean;
  onSelectChange: (campaignId: string, checked: boolean) => void;
  refetchCampaigns: () => void; // ✅ PHASE 1A: Prop pour passer aux enfants
}

export const CampaignTableRow = ({ campaign, isSelected, onSelectChange, refetchCampaigns }: CampaignTableRowProps) => {
  const { toast } = useToast();

  // Calculer le nombre de référents actifs (demandés - refusés)
  const activeReferentsCount = (campaign.referents_requested_count || campaign.referents_count) - (campaign.refused_referents || 0);

  // Fonction pour formater la date avec gestion d'erreurs
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      const userError = ErrorHandler.handle(error as Error, {
        component: 'CampaignTableRow',
        function: 'formatDate',
        campaignId: campaign.id
      });

      toast({
        title: "Erreur de formatage",
        description: userError.message,
        variant: "destructive"
      });

      return 'Date invalide';
    }
  };

  return (
    <TableRow className="group border-b border-[hsl(var(--app-border-subtle))] transition-colors hover:bg-[hsl(var(--app-surface-muted))]/40">
      <TableCell className="w-12 px-4 py-3.5 align-middle">
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelectChange(campaign.id, checked as boolean)}
          className="border-[hsl(var(--app-border-subtle))] data-[state=checked]:border-[hsl(var(--app-accent))] data-[state=checked]:bg-[hsl(var(--app-accent))]"
        />
      </TableCell>
      <TableCell className="py-3.5 align-middle">
        <div className="text-sm font-medium text-[hsl(var(--app-text-strong))]">
          {campaign.candidate_name || 'Nom non disponible'}
        </div>
      </TableCell>
      <TableCell className="py-3.5 align-middle">
        <div className="text-sm text-[hsl(var(--app-text-muted))]">
          {campaign.target_position || 'Poste non spécifié'}
        </div>
      </TableCell>
      <TableCell className="py-3.5 align-middle">
        <div className="text-sm text-[hsl(var(--app-text-muted))]">
          {formatDate(campaign.created_at)}
        </div>
      </TableCell>
      <TableCell className="py-3.5 align-middle">
        <CampaignStatusBadge
          status={campaign.status}
          completedReferents={campaign.completed_referents}
          totalReferents={activeReferentsCount}
        />
      </TableCell>
      <TableCell className="py-3.5 text-left align-middle">
        <CampaignActionsIcons
          campaignId={campaign.id}
          campaignStatus={campaign.status}
          completedReferents={campaign.completed_referents}
          totalReferents={activeReferentsCount}
          isDraft={campaign.is_draft}
          candidateName={campaign.candidate_name}
          refetchCampaigns={refetchCampaigns} // ✅ PHASE 1A: Passé en prop
        />
      </TableCell>
    </TableRow>
  );
};
